import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  Collapse,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { fetchAPI } from '../../../api/request';
import Alert from '../../common/Alert.component';
import Loading from '../../common/Loading.component';

const NoteContainer = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg,rgb(252, 200, 121) 0%,rgb(253, 237, 185) 100%)',
  borderRadius: '10px',
  padding: theme.spacing(2),
  boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  minHeight: '55px',
  display: 'flex',
  flexDirection: 'column',
}));

const MAX_HEIGHT = 55;

const NotesBox = ({
  jwt,
  yachtID,
  initialNote,
  expandedNote,
  setExpandedNote,
}) => {
  const [note, setNote] = useState(initialNote);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    setNote(initialNote);
  }, [initialNote]);

  const checkOverflow = useCallback(() => {
    requestAnimationFrame(() => {
      if (contentRef.current) {
        setIsOverflowing(
          contentRef.current.scrollHeight > contentRef.current.clientHeight
        );
      }
    });
  }, []);

  useEffect(() => {
    if (!isEditing) {
      checkOverflow();
    }
  }, [isEditing, checkOverflow]);

  useEffect(() => {
    checkOverflow();
  }, [note, checkOverflow]);

  const handleSave = useCallback(() => {
    setLoading(true);
    setSuccess(null);
    setError(null);

    fetchAPI(
      {
        path: `/yachts/${yachtID}`,
        method: 'PUT',
        auth: true,
      },
      {},
      '',
      { data: { wall: note } },
      jwt
    )
      .then(() => {
        setLoading(false);
        setSuccess(true);
        setIsEditing(false);
        setExpandedNote(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
    // eslint-disable-next-line
  }, [jwt, note, yachtID]);

  return (
    <NoteContainer>
      {isEditing ? (
        <Fragment>
          <TextField
            multiline
            fullWidth
            variant='standard'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            InputProps={{
              disableUnderline: true,
              style: {
                minHeight: MAX_HEIGHT,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                fontFamily: 'Verdana',
                fontSize: 13,
              },
            }}
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              pt: 1.5,
            }}
          />
          <Box sx={{ textAlign: 'right' }}>
            <IconButton
              onClick={handleSave}
              size='small'
              sx={{ position: 'absolute', top: 5, right: 5 }}
            >
              <SaveIcon fontSize='small' />
            </IconButton>
          </Box>
        </Fragment>
      ) : (
        <Box>
          <Collapse in={expandedNote} collapsedSize={MAX_HEIGHT}>
            <Typography
              variant='body2'
              ref={contentRef}
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#555',
                fontSize: 13,
                pt: 1.5,
                maxHeight: expandedNote ? 'none' : `${MAX_HEIGHT}px`,
                overflow: 'hidden',
                fontFamily: 'Verdana',
              }}
            >
              {note || 'Add note...'}
            </Typography>
          </Collapse>

          <Box>
            {(isOverflowing || expandedNote) && (
              <Box sx={{ position: 'absolute', top: 10, right: 40 }}>
                <Link
                  component='button'
                  variant='body2'
                  onClick={() => setExpandedNote((prev) => !prev)}
                >
                  {expandedNote ? 'Show less' : 'Show more'}
                </Link>
              </Box>
            )}

            <Tooltip title='Edit note'>
              <IconButton
                onClick={() => setIsEditing(true)}
                size='small'
                sx={{ position: 'absolute', top: 5, right: 5 }}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {error && <Alert severity='error'>An error has occurred</Alert>}
      {success && <Alert severity='success'>Note saved successfully!</Alert>}
      {loading && <Loading />}
    </NoteContainer>
  );
};

export default NotesBox;
